import { useQueries, type QueryStatus } from '@tanstack/react-query'
import { mergeProductsShippingOptions } from 'features/productArrivals/cartShippingOptions'
import {
  getProductDeliveryPromiseQueryOptions,
  type DeliveryPromisesQueryInputs,
} from 'features/productArrivals/deliveryPromisesQuery'

interface UseCartDeliveryPromisesQueryParams {
  deliveryPromisesQueryInputs: DeliveryPromisesQueryInputs
  enabled: boolean
}

// Checkout offers the shipping methods every product has, so the cart's options exist only once
// each product's promise has loaded.
export function useCartDeliveryPromisesQuery({
  deliveryPromisesQueryInputs,
  enabled,
}: UseCartDeliveryPromisesQueryParams) {
  const { deliveryPromiseRequests, ...sharedInputs } =
    deliveryPromisesQueryInputs
  return useQueries({
    queries: deliveryPromiseRequests.map((deliveryPromiseRequest) => ({
      ...getProductDeliveryPromiseQueryOptions({
        ...sharedInputs,
        deliveryPromiseRequest,
      }),
      enabled,
    })),
    combine: (productQueries) => {
      const productsShippingOptions = productQueries.flatMap(
        (productQuery, index) =>
          productQuery.isSuccess
            ? [
                {
                  productType: deliveryPromiseRequests[index].productType,
                  shippingOptions: productQuery.data,
                },
              ]
            : []
      )
      const isError = productQueries.some(({ isError }) => isError)
      const isSuccess =
        productQueries.length > 0 &&
        productsShippingOptions.length === productQueries.length
      const status: QueryStatus = isError
        ? 'error'
        : isSuccess
        ? 'success'
        : 'pending'
      return {
        data: isSuccess
          ? mergeProductsShippingOptions(productsShippingOptions)
          : undefined,
        error: productQueries.find(({ error }) => error)?.error ?? null,
        isError,
        isSuccess,
        isFetching: productQueries.some(({ isFetching }) => isFetching),
        dataUpdatedAt: Math.max(
          ...productQueries.map(({ dataUpdatedAt }) => dataUpdatedAt)
        ),
        status,
      }
    },
  })
}
